import React from 'react';
import { Button } from '@ui';
import { DropdownMenuContainer } from './DropdownMenu';
import { HEADER_NAV_REGION_ID } from './navRegion';

type LayoutLocale = 'en' | 'es';
type HeaderNavItemLink = { url: string; text?: string };
type HeaderNavSection = {
  section_title: string;
  links: HeaderNavItemLink[];
  isDropdown: boolean;
};

interface HeaderNavProps {
  menuOpen: boolean;
  sections?: HeaderNavSection[];
  currentPath?: string;
  locale?: LayoutLocale;
  resolveLinkHref?: (href: string, locale: LayoutLocale) => string;
}

const DEFAULT_SECTIONS: HeaderNavSection[] = [
  { section_title: 'Home', links: [{ url: '/' }], isDropdown: false },
  { section_title: 'About', links: [{ url: '/about' }], isDropdown: false },
  {
    section_title: 'Portfolio',
    links: [
      { text: 'Overview', url: '/portfolio' },
      { text: 'Web', url: '/portfolio/web' },
      { text: 'Vector Art', url: '/portfolio/vector' },
      { text: 'Traditional Art', url: '/portfolio/pen-and-ink' },
      { text: 'Lettering Art', url: '/portfolio/lettering' },
      { text: 'Digital Art', url: '/portfolio/digital-art' },
      { text: '3D Modeling (CGI)', url: '/portfolio/3d-cgi' },
    ],
    isDropdown: true,
  },
  { section_title: 'Design system', links: [{ url: '/design-system' }], isDropdown: false },
];

const defaultResolveLinkHref = (href: string, _locale: LayoutLocale) => href;

const HeaderNav: React.FC<HeaderNavProps> = ({
  menuOpen,
  sections = DEFAULT_SECTIONS,
  currentPath = '/',
  locale = 'en',
  resolveLinkHref = defaultResolveLinkHref,
}) => {
  const navRef = React.useRef<HTMLElement>(null);
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    }
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, []);

  const isNavHidden = isMobile && !menuOpen;

  React.useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    if (isNavHidden) {
      nav.setAttribute('inert', '');
    } else {
      nav.removeAttribute('inert');
    }
  }, [isNavHidden]);

  const getPathFromUrl = (url?: string) => {
    if (!url) return '/';
    try {
      return new URL(url, 'http://example.com').pathname.replace(/\/+$/, '') || '/';
    } catch {
      return url.replace(/\/+$/, '') || '/';
    }
  };

  const isActiveLink = (linkUrl?: string) => {
    if (!linkUrl) return false;
    const linkPath = getPathFromUrl(linkUrl);
    const current = currentPath.replace(/\/+$/, '') || '/';
    if (linkPath === '/') return current === '/';
    return current === linkPath || current.startsWith(linkPath + '/');
  };

  const toMenuLabel = (href: string, fallback: string) => {
    const segment = getPathFromUrl(href).split('/').filter(Boolean).pop();
    if (!segment) return fallback;
    return segment.replace(/[-_]+/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
  };

  return (
    <nav
      ref={navRef}
      className={`nav-links${menuOpen ? ' open' : ''}`}
      id={HEADER_NAV_REGION_ID}
      aria-hidden={isNavHidden}
    >
      {Array.isArray(sections) &&
        sections.map((section, idx) => {
          if (!section.isDropdown) {
            // Render first link in section as Button
            const link = section.links[0];
            let href = link.url || '/';
            try {
              href = resolveLinkHref(href, locale);
            } catch {
              // keep original href on error
            }
            const active = isActiveLink(href);
            return (
              <Button
                key={section.section_title + idx}
                as='a'
                href={href}
                className={`btn-link nav-link${active ? ' active' : ''}`}
                label={section.section_title}
                tabIndex={isNavHidden ? -1 : undefined}
              />
            );
          } else {
            const dropdownItems = section.links
              .map((link) => {
                const rawUrl = link.url || '/';
                let href = rawUrl;
                try {
                  href = resolveLinkHref(rawUrl, locale);
                } catch {
                  // keep original href on error
                }
                const text = link.text || toMenuLabel(href, section.section_title);
                return { text, href };
              })
              .filter((item) => item.href);

            const rawFirst = section.links[0]?.url || '/';
            const firstPath = getPathFromUrl(rawFirst);
            const segs = firstPath.split('/').filter(Boolean);
            const parentRaw =
              segs.length > 1
                ? `/${segs.slice(0, -1).join('/')}`
                : segs.length === 1
                  ? `/${segs[0]}`
                  : '/';
            let triggerHref = parentRaw;
            try {
              triggerHref = resolveLinkHref(parentRaw, locale);
            } catch {
              // keep parentRaw on error
            }
            const triggerActive = isActiveLink(triggerHref);

            // Render DropdownMenuContainer for dropdowns
            return (
              <DropdownMenuContainer
                key={section.section_title + idx}
                label={section.section_title}
                menuId={`${section.section_title.toLowerCase().replace(/\s+/g, '-')}-${idx}`}
                menuClassName='dropdown-menu'
                items={dropdownItems}
                triggerHref={triggerHref}
                triggerActive={triggerActive}
                tabIndex={isNavHidden ? -1 : undefined}
                locale={locale}
              />
            );
          }
        })}
    </nav>
  );
};

export type { HeaderNavSection };
export default HeaderNav;
